import { useEffect, useState } from 'react'
import dayjs from 'dayjs'

import { useAppSelector } from 'src/store/hooks'
import { selectScheduledMaintenance } from 'src/v3/settings/settings.slice'

export type MaintenanceTier = 'none' | 'warning' | 'countdown'

export type MaintenanceTimerState = {
  tier: MaintenanceTier
  minutesUntil: number
  countdownText: string
}

let formatCountdown = (totalSeconds: number): string => {
  let h = Math.floor(totalSeconds / 3600)
  let m = Math.floor((totalSeconds % 3600) / 60)
  let s = totalSeconds % 60
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':')
}

let computeState = (startTime: string | null): MaintenanceTimerState => {
  if (!startTime) {
    return { tier: 'none', minutesUntil: Infinity, countdownText: '' }
  }

  let secondsUntil = dayjs(startTime).diff(dayjs(), 'second')
  let minutesUntil = secondsUntil / 60

  if (minutesUntil <= 0) {
    return { tier: 'none', minutesUntil: 0, countdownText: '' }
  }

  if (minutesUntil <= 15) {
    return {
      tier: 'countdown',
      minutesUntil,
      countdownText: formatCountdown(Math.max(0, secondsUntil)),
    }
  }

  if (minutesUntil <= 720) {
    return { tier: 'warning', minutesUntil, countdownText: '' }
  }

  return { tier: 'none', minutesUntil, countdownText: '' }
}

export let useMaintenanceTimer = (): MaintenanceTimerState => {
  let maintenance = useAppSelector(selectScheduledMaintenance)
  let startTime = maintenance?.startTime ?? null

  let [state, setState] = useState<MaintenanceTimerState>(() =>
    computeState(startTime),
  )

  // Recompute immediately when startTime changes (config loaded or cleared)
  useEffect(() => {
    setState(computeState(startTime))
  }, [startTime])

  // Set up a timer appropriate for the current tier.
  // Re-runs when startTime or tier changes, swapping to the right interval each time.
  useEffect(() => {
    if (!startTime) return undefined

    let current = computeState(startTime)

    if (current.minutesUntil > 720) {
      // Sleep until the 12h warning window opens, then re-evaluate
      let msUntilWindow = (current.minutesUntil - 720) * 60 * 1_000
      let timeout = setTimeout(
        () => setState(computeState(startTime)),
        msUntilWindow,
      )
      return () => clearTimeout(timeout)
    }

    if (current.tier === 'warning') {
      // Slow poll — banner shows h/m so 60s resolution is sufficient
      let interval = setInterval(
        () => setState(computeState(startTime)),
        60_000,
      )
      return () => clearInterval(interval)
    }

    if (current.tier === 'countdown') {
      // Fast poll for live HH:MM:SS countdown
      let interval = setInterval(() => setState(computeState(startTime)), 1_000)
      return () => clearInterval(interval)
    }

    // tier === 'none' with minutesUntil <= 0: maintenance has started, no timer needed
    return undefined
  }, [startTime, state.tier])

  return state
}
